/**
 * Customer UI translation overrides.
 *
 * White-label overrides: whiteLabelCode -> locale -> { key: value }
 * Store overrides:       storeId        -> locale -> { key: value }
 *
 * Store overrides win over white-label overrides for the same key.
 * Only include keys that differ from standard translations.
 */
// STORE_OVERRIDES = { '1003': { 'en': { 'public.dashboard.basket.emptyCart': 'Your cart is empty:1003' } } };
export const WHITE_LABEL_OVERRIDES: Record<
  string,
  Record<string, Record<string, string>>
> = {
  Stcmarketplace: {
    el: { 'public.checkout.deliveryFee': 'Διανομή' },
  },
};

// PAUL Greece loyalty app (#11489, PAUL feedback of 2026-09-01; login screen
// restructured by #11687, PAUL feedback of 2026-10-01). PAUL reviewed
// the English UI and asked for this copy in its place, so the same block is
// applied to both locales, except the login lines in PAUL_GREECE_ENGLISH_TEXTS
// (PAUL translation sheet, 2026-10-06). The Template App opens parent store
// 6697 and switches to the branch's own store id once a branch is opened, so
// every PAUL store id carries the block; a new PAUL branch must be added below.
const PAUL_GREECE_TEXTS: Record<string, string> = {
  // Home / login page title (4.3a / 5.3a)
  'public.bottomBar.loyaltyPromotions': 'PAUL Greece Loyalty',
  // Home greeting (5.4)
  'public.loyaltyPromotions.helloName': 'Bonjour {{name}}!',
  'public.loyaltyPromotions.hello': 'Bonjour!',
  // Login screen box 3, the line above the login card; rendered as rich text
  'public.checkout.loginToContinue': '**Καλώς ήρθες στο PAUL Loyalty!**',
  // Login block welcome text (4.6a); its first line moved up to box 3. The 3%
  // line is its bold first line, a heading for "Κέρδισε…" (PAUL change list of
  // 2026-10-13, C5, #11854); the sign-up line is left to box 4 below
  'public.checkout.prompt.signin':
    '**🏆 3% επιστροφή σε κάθε αγορά Take Away**\nΚέρδισε επιστροφή στο wallet σου με κάθε αγορά Take Away\n\n**Άμεση εξαργύρωση**\nΧρησιμοποίησε όλο ή μέρος του διαθέσιμου υπολοίπου σου σε επόμενη αγορά\n\n**Προνόμια & προσφορές**\nΜείνε ενημερωμένος για ειδικές ενέργειες και αποκλειστικές προσφορές',
  // Login screen box 4, above the sign-in buttons. The 3% sentence is in the
  // welcome text above, so the trophy line is not shown: '' on
  // public.loyalty.reward hides it through
  // CustomerUiTranslationOverrideService.isBlanked, not through the
  // translation, which would fall back to the English bundle text (#11854).
  'public.loyalty.prompt': 'Κάνε την εγγραφή σου ή συνδέσου για να συνεχίσεις',
  'public.loyalty.reward': '',
  // Wallet summary (5.5a), Elina's wording of 2026-10-13 (#11854)
  'public.profile.loyaltyPointsCurrent': 'Το PAUL Wallet σου – Διαθέσιμα:',
  'public.loyalty.noPoints': 'Κέρδισε 3% επιστροφή με κάθε αγορά Take Away.',
  // Locations page title (7.2a)
  'public.stores.title': 'Our Locations',
  // Profile page (8.2a / 8.3a / 8.4 / 8.5a)
  'public.profile.myAccountTitle': 'Profile',
  'public.profile.myProfileTitle': 'Προσωπικά στοιχεία',
  'public.profile.loyaltyPointsTitle': 'PAUL Wallet',
  'public.profile.myOrdersTitle': 'Οι αγορές μου',
  'public.profile.viewQrCodeTitle': 'Το PAUL QR μου',
  'public.profile.name': 'Ονοματεπώνυμο',
  // Bottom tab bar (3.1b)
  'public.bottomBar.stores': 'Locations',
  'public.bottomBar.myAccount': 'Profile',
};

// The login lines PAUL gave in English; box 3 stays Greek in both locales
const PAUL_GREECE_ENGLISH_TEXTS: Record<string, string> = {
  'public.checkout.prompt.signin':
    '**🏆 3% cashback on every Take Away purchase**\nEarn cashback to your wallet with every Take Away purchase\n\n**Instant redemption**\nUse all or part of your available balance on your next purchase\n\n**Perks & offers**\nStay up to date on special promotions and exclusive offers',
  'public.loyalty.prompt': 'Sign up or log in to continue',
  'public.loyalty.reward': '',
};

const PAUL_GREECE_OVERRIDES: Record<string, Record<string, string>> = {
  en: { ...PAUL_GREECE_TEXTS, ...PAUL_GREECE_ENGLISH_TEXTS },
  el: { ...PAUL_GREECE_TEXTS },
};

// The Foodie bar kiosks on Point2Order, Athens and Paros (#11833, PO request
// of 2026-10-19). The kiosks have an external QR scanner that fills the
// voucher field, so its label asks the customer to scan instead of typing a
// code. Point2Order numbers its stores in its own sequence, so these ids are
// also old GonnaOrder trial stores; all of them had ordering disabled on
// 2026-10-19, so their checkout is not reachable. A new Foodie store must be
// added below.
const FOODIE_P2O_OVERRIDES: Record<string, Record<string, string>> = {
  en: { 'public.checkout.voucherCode': 'Scan QR code' },
  el: { 'public.checkout.voucherCode': 'Σκάναρε το QR' },
};

export const STORE_OVERRIDES: Record<
  string,
  Record<string, Record<string, string>>
> = {
  '6697': PAUL_GREECE_OVERRIDES,
  '6698': PAUL_GREECE_OVERRIDES,
  '6700': PAUL_GREECE_OVERRIDES,
  '6702': PAUL_GREECE_OVERRIDES,
  '6704': PAUL_GREECE_OVERRIDES,
  '6706': PAUL_GREECE_OVERRIDES,
  '7078': PAUL_GREECE_OVERRIDES,
  '1013': FOODIE_P2O_OVERRIDES,
  '1014': FOODIE_P2O_OVERRIDES,
  '1015': FOODIE_P2O_OVERRIDES,
  '1016': FOODIE_P2O_OVERRIDES,
  '1017': FOODIE_P2O_OVERRIDES,
  '1018': FOODIE_P2O_OVERRIDES,
  '1022': FOODIE_P2O_OVERRIDES,
  '1043': FOODIE_P2O_OVERRIDES,
  '1044': FOODIE_P2O_OVERRIDES,
  '1045': FOODIE_P2O_OVERRIDES,
  '6064': {
    el: { 'public.checkout.deliveryFee': 'Διανομή' },
  },
  '5982': {
    el: { 'public.checkout.deliveryFee': 'Διανομή' },
  },
  '6109': {
    el: { 'public.checkout.deliveryFee': 'Διανομή' },
  },
  '6110': {
    el: { 'public.checkout.deliveryFee': 'Διανομή' },
  },
  '6085': {
    el: { 'public.checkout.deliveryFee': 'Διανομή' },
  },
  '6559': {
    en: {
      'public.checkout.yourOrder': 'Your booking',
      'public.checkout.title.serve.known.location':
        'Your room and personal details',
      'public.checkout.title.serve.unknown.location':
        'Your room and personal details',
      'public.checkout.wishTimeHeading': 'Select your preferred time',
      'public.checkout.youritems': 'Your booking details',
      'public.payment.proceedToPayment': 'Pay €20 & confirm booking',
      'public.checkout.termsAndPolicyImplicit':
        'Pay €20 now to reserve your massage. Within 1 hour our partners will call you via phone or whatsapp to confirm the booking. The remaining balance will be paid after. By proceeding you accept the',
      'public.precheckout.title':
        'Enter your breakfast order number to book your in-room massage',
      'public.precheckout.message':
        'Enter your breakfast code to book your in-room massage.\nWe’ll use your breakfast code to locate your room and send the therapist directly to you.\n✔ No registration needed\n✔ No extra steps\n✔ Takes 10 seconds',
      'public.precheckout.orderNumberLabel':
        'Breakfast order number (example:A5-B1)',
    },
  },
  '6903': {
    en: {
      'public.itemDetails.addToOrder': 'Add to enquiry',
      'public.global.viewOrder': 'View enquiry',
      'public.checkout.yourOrder': 'Your Enquiry',
      'public.checkout.wishTimeHeading': 'Date of your event',
      'public.checkout.yourAddress': 'Event Address',
      'public.payment.submitOrder': 'Submit your enquiry',
    },
  },
};
